import { env } from '@skin-seoul/env-utils';

/**
 * Send event to Google Tag Manager dataLayer
 * @param eventData - Event data to send to GTM
 */
export const sendGTMEvent = (eventData: Record<string, unknown>): void => {
  if (typeof window === 'undefined') {
    console.warn('[GTM] Cannot send event: window is undefined');
    return;
  }

  // Initialize dataLayer if it doesn't exist
  if (!window.dataLayer) {
    console.warn('[GTM] dataLayer not found, creating it');
    window.dataLayer = [];
  }

  // Push event to dataLayer
  window.dataLayer.push(eventData);

  // Enhanced logging
  console.info('[GTM Event sent]:', eventData);
  console.info('[GTM] Current dataLayer length:', window.dataLayer.length);
  console.info('[GTM] Last 3 dataLayer entries:', window.dataLayer.slice(-3));

  // Log in development mode
  if (process.env.NODE_ENV === 'development') {
    console.info('[GTM Debug] Event details:', {
      eventData,
      dataLayerExists: Boolean(window.dataLayer),
      dataLayerLength: window.dataLayer?.length,
      gtmLoaded: Boolean(window.google_tag_manager),
    });
  }
};

/**
 * Initialize GTM (Google Tag Manager)
 * Loads the GTM script and sets up the data layer
 */
export function initializeGTM(): void {
  const gtmId = env('NEXT_PUBLIC_GTM_ID');

  if (!gtmId) {
    console.warn('[GTM] GTM ID not found in environment variables');
    return;
  }

  console.info(`[GTM] Starting initialization with ID: ${gtmId}`);

  // Create dataLayer if it doesn't exist
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    // Check if script already exists
    const existingScript = document.querySelector(`script[src*="gtm.js?id=${gtmId}"]`);
    if (existingScript) {
      console.info('[GTM] Script already exists, skipping load');
      return;
    }

    // Load GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;

    script.onload = () => {
      console.info(`[GTM] Script loaded successfully for container ${gtmId}`);
      console.info(`[GTM] Google Tag Manager available:`, Boolean(window.google_tag_manager));
    };

    script.onerror = (error) => {
      console.error(`[GTM] Failed to load script for container ${gtmId}:`, error);
    };

    document.head.appendChild(script);

    console.info(`[GTM] Script tag added to document head`);

    // Test dataLayer after a short delay
    setTimeout(() => {
      console.info(`[GTM] Post-init check - dataLayer:`, window.dataLayer);
      console.info(`[GTM] Post-init check - GTM loaded:`, Boolean(window.google_tag_manager));
    }, 2000);
  }
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
    google_tag_manager?: unknown;
  }
}
